'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSecureEmbedURL } from '@/features/video/actions';
import { useIframeCleanup } from './use-iframe-cleanup';
gsap.registerPlugin(useGSAP);
type PlayerIframeProps = {
  videoId: string;
  onEnded?: () => void;
  pause?: boolean;
};
export function PlayerIframe({
  videoId,
  onEnded,
  pause
}: PlayerIframeProps) {
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<any>(null);
  const active = useRef(true);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [PlayerConstructor, setPlayerConstructor] = useState<any>(null);
  const isActivePath = useCallback((pathname: string) => pathname.startsWith('/watch/'), []);
  const beforeBlank = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.off('ready');
        playerRef.current.off('ended');
      } catch {}
    }
  }, []);
  useIframeCleanup(iframeRef, {
    isActivePath,
    beforeBlank
  });
  useGSAP(() => {
    const iframeContainer = iframeContainerRef.current;
    const iframe = iframeContainer?.querySelector('iframe');
    if (iframe) {
      gsap.set(iframe, {
        opacity: 0,
        scale: 0.95
      });
      gsap.to(iframe, {
        opacity: 1,
        scale: 1,
        duration: 1,
        ease: 'sine.out',
        delay: 1
      });
    }
  }, {
    dependencies: [embedUrl]
  });
  useEffect(() => {
    import('player.js').then(module => {
      setPlayerConstructor(() => module.default.Player);
    });
  }, []);
  useEffect(() => {
    active.current = true;
    const fetchUrl = async () => {
      try {
        const url = await getSecureEmbedURL(videoId);
        setEmbedUrl(url);
      } catch (error) {
        console.error('URL fetch failed:', error);
      }
    };
    fetchUrl();
  }, [videoId]);
  useEffect(() => {
    active.current = true;
    const playerContainer = iframeContainerRef.current;
    if (playerContainer && embedUrl && PlayerConstructor) {
      const iframe_0 = playerContainer.querySelector('iframe');
      if (iframe_0?.src) {
        playerRef.current = new PlayerConstructor(iframe_0);
        playerRef.current.on('ready', () => {
          if (!active.current) return;
          try {
            if (pause) playerRef.current.pause();
          } catch {}
          playerRef.current.on('ended', () => {
            if (!active.current) return;
            if (onEnded) onEnded();
          });
        });
      }
    }
    return () => {
      active.current = false;
      if (playerRef.current) {
        try {
          playerRef.current.pause();
          playerRef.current.off('ready');
          playerRef.current.off('ended');
        } catch {}
      }
    };
  }, [videoId, pause, embedUrl, onEnded, PlayerConstructor]);
  if (!embedUrl) {
    return null;
  }
  return <div className='absolute inset-0 min-h-svh'>
      <div className='h-full w-full' ref={iframeContainerRef}>
        <iframe ref={iframeRef} title={`Video player for ${videoId}`} src={embedUrl} loading='lazy' allow='accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;' allowFullScreen className='h-full w-full border-0 bg-black' />
      </div>
    </div>;
}